<script setup>
import Draggable from 'vuedraggable';
import CardStageBadge from './CardStageBadge.vue';
import CardsKanbanItem from './CardsKanbanItem.vue';

defineProps({
  // Each stage carries the `index` it has on the pipeline, which colors its badge.
  stages: { type: Array, required: true },
  columns: { type: Object, required: true },
  // The board's names for the card statuses.
  statusLabels: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['open', 'move', 'loadMore']);

// Bound as an object: vuedraggable hands its attrs to Sortable as they come, so a shorthand
// boolean attribute (`delay-on-touch-only`) arrives as "" and is read as false.
// A touch picks a card up only after a 200ms press, so a swipe still scrolls the board; the
// finger may wobble 5px meanwhile. The fallback drags by pointer events on mouse and touch
// alike, and with it the board scrolls toward the column under a card held near its edge.
const sortableOptions = {
  ghostClass: 'opacity-40',
  delay: 200,
  delayOnTouchOnly: true,
  touchStartThreshold: 5,
  forceFallback: true,
  scrollSensitivity: 60,
};

// The buzz tells a finger that the long press picked the card up.
const onChoose = () => navigator.vibrate?.(15);

// A card crossing columns raises `removed` on its source too; only the target reports it.
const onChange = (stage, event) => {
  const change = event.added || event.moved;
  if (!change) return;

  emit('move', change.element, {
    stageId: stage.id,
    newIndex: change.newIndex,
    oldIndex: change.oldIndex,
  });
};
</script>

<template>
  <div class="flex h-full gap-3 px-4 pb-4 overflow-x-auto sm:px-6">
    <section
      v-for="stage in stages"
      :key="stage.id"
      class="flex flex-col flex-shrink-0 h-full w-[85vw] max-w-72 rounded-xl bg-n-alpha-1"
      :data-stage-id="stage.id"
    >
      <header class="flex items-center gap-2 px-3 pt-3 pb-2">
        <CardStageBadge :stage="stage" :index="stage.index" />
        <span class="text-xs text-n-slate-10" data-testid="column-count">
          {{ columns[stage.id].count }}
        </span>
      </header>
      <Draggable
        :list="columns[stage.id].cards"
        group="cards"
        item-key="id"
        v-bind="sortableOptions"
        class="flex flex-col flex-1 gap-2 px-2 pb-2 overflow-y-auto min-h-16"
        @choose="onChoose"
        @change="onChange(stage, $event)"
      >
        <!-- Sortable marks the held card `sortable-chosen` and the clone following the pointer
             `sortable-drag`; it takes one class each, so the looks live here as variants. -->
        <template #item="{ element }">
          <CardsKanbanItem
            :card="element"
            :status-labels="statusLabels"
            class="[-webkit-touch-callout:none] [&.sortable-chosen]:border-n-brand [&.sortable-chosen]:shadow-lg [&.sortable-drag]:rotate-2"
            @open="emit('open', $event)"
          />
        </template>
        <!-- Explicit keys: vuedraggable renders the footer among the cards, whose keys are
             their ids, and a v-if branch's implicit key (0, 1) would collide with card #1. -->
        <template #footer>
          <p
            v-if="!columns[stage.id].cards.length"
            key="column-empty"
            class="py-6 mb-0 text-xs text-center text-n-slate-10"
          >
            {{ $t('CARDS.INDEX.COLUMN_EMPTY') }}
          </p>
          <button
            v-else-if="columns[stage.id].cards.length < columns[stage.id].count"
            key="column-load-more"
            type="button"
            data-testid="column-load-more"
            class="py-2 text-xs rounded-lg text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
            @click="emit('loadMore', stage)"
          >
            {{ $t('CARDS.INDEX.LOAD_MORE') }}
          </button>
        </template>
      </Draggable>
    </section>
  </div>
</template>
